/**
 * 合并多个 React ref 的工具函数
 * 
 * ⚠️ 关键：必须支持 null，否则切视频/卸载时 ref 会被 React 调 null，导致 HLS 不初始化
 * 
 * 使用场景：
 * - 合并 callback ref 和外部 ref
 * - 支持多个 ref 同时绑定到同一个元素
 * 
 * @param refs 要合并的 ref 数组（可以是 RefObject、callback ref、null、undefined）
 * @returns 合并后的 callback ref
 * 
 * @example
 * ```tsx
 * const videoRef = useRef<HTMLVideoElement>(null);
 * const { videoCallbackRef } = useHlsPlayer({ ... });
 * 
 * <video ref={mergeRefs(videoCallbackRef, videoRef)} />
 * ```
 */
export function mergeRefs<T>(
  ...refs: (React.Ref<T> | null | undefined)[]
): React.RefCallback<T> {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        // callback ref 必须能接收 null
        ref(value);
      } else if (ref != null) {
        // ref.current 必须能设置为 null
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

